/**
 * Adapters between Solara Core DTOs (@solara/api-client) and the
 * SampleInvoice/SampleLineItem shape used by the existing UI components.
 *
 * BigInt minor-unit fields come over the wire as decimal strings. The
 * minorToAed helper converts them to whole-AED numbers for display.
 *
 * Only InvoiceDto → SampleInvoice is adapted here; other resources
 * (VAT, Spend, Recovery, Inbox, Approvals, Templates) have shapes that
 * diverge sharply from the mocks, so those pages render against the
 * DTOs directly.
 */
import type { InvoiceDto } from '@solara/api-client';
import type {
  InvoiceDirection,
  MlsStatus,
  SampleInvoice,
  SampleLineItem,
  VatRate,
} from '@solara/sample-data';

export function minorToAed(minor: string | number | null | undefined): number {
  if (minor == null) return 0;
  const n = typeof minor === 'string' ? Number(minor) : minor;
  if (!Number.isFinite(n)) return 0;
  return n / 100;
}

function statusToMls(inv: InvoiceDto): MlsStatus {
  // Map fine-grained backend status onto the 4-state MLS code the UI
  // already renders. Anything mid-pipeline collapses to PENDING.
  if (inv.mlsState === 'AB') return 'AB';
  if (inv.mlsState === 'AP') return 'AP';
  if (inv.mlsState === 'RE') return 'RE';
  if (inv.status === 'ACKNOWLEDGED' || inv.status === 'ARCHIVED') return 'AP';
  if (
    inv.status === 'DELIVERED' ||
    inv.status === 'SENDING' ||
    inv.status === 'SIGNED'
  )
    return 'AB';
  if (inv.status === 'REJECTED' || inv.status === 'FAILED') return 'RE';
  return 'PENDING';
}

function toVatRate(raw: string): VatRate {
  const n = Number(raw);
  if (n === 5) return 5;
  if (n === 0) return 0;
  return 'EXEMPT';
}

export function adaptInvoice(inv: InvoiceDto): SampleInvoice {
  const lineItems: SampleLineItem[] = (inv.lineItems ?? []).map((li) => ({
    description: li.description,
    descriptionAr: undefined,
    quantity: Number(li.quantity) || 1,
    unitPrice: minorToAed(li.unitPriceMinor),
    vatRate: toVatRate(li.vatRate),
    lineNet: minorToAed(li.netMinor),
    lineVat: minorToAed(li.vatMinor),
  }));
  return {
    invoiceId: inv.id,
    invoiceNumber: inv.invoiceNumber,
    tenantId: inv.tenantId,
    sellerTrn: inv.sellerTrn,
    sellerName: inv.sellerName,
    buyerTrn: inv.buyerTrn,
    buyerName: inv.buyerName,
    buyerNameAr: inv.buyerName, // backend has no Arabic name field
    // Sample-data utilities assume YYYY-MM-DD; the backend ships full ISO
    // timestamps. Slice so timeline / archive generators don't end up with
    // strings like `"2026-05-07T...T08:00:00Z"` that parse to Invalid Date.
    issueDate: inv.issueDate ? inv.issueDate.slice(0, 10) : inv.issueDate,
    deliveryDate: inv.deliveryDate ? inv.deliveryDate.slice(0, 10) : null,
    currency: 'AED',
    lineItems,
    totalNetAmount: minorToAed(inv.subtotalMinor),
    totalVatAmount: minorToAed(inv.vatMinor),
    totalGrossAmount: minorToAed(inv.totalMinor),
    mlsStatus: statusToMls(inv),
    direction: inv.direction as InvoiceDirection,
    idempotencyKey: inv.id,
    attachments: (inv.attachments ?? []).map((a) => ({
      name: a.fileName,
      sizeBytes: a.sizeBytes,
      mime:
        (a.mimeType as 'application/pdf' | 'image/jpeg' | 'image/png') ??
        'application/pdf',
      path: a.storageKey,
    })),
  };
}

export function adaptInvoices(
  list: InvoiceDto[] | undefined,
): SampleInvoice[] {
  return (list ?? []).map(adaptInvoice);
}
